'use strict';
/* ==========================================================================
   levels/song.js — step "song" (any): DIFFERENT JOBS, the funk song (DESIGN.md §5.4) on the blueprint wall, with
   karaoke. The machinery (song clock, <audio> stream, karaoke band, portrait dock, controls, completion) is
   lessons/blue-eyes/levels/song.js's; the stage is this lesson's (core-local/fair.js: blue WHAT law cards, amber WHY
   theory cards, the tri-fold board, the graph-paper inset).

   ► The song: assets/music/song.m4a = ACE-Step take funk_alt_7 (104 bpm sheet, 80 s; assets/ASSETS.md).
     Timings: assets/music/song_words.json (tools/song_timing.py): the TRUE lyric lines of content/song.json (rev 2,
     21 lines, every one sung) with word times from the take's Whisper passes, and the beat grid {bpm 105, offset =
     first downbeat ≈ 0.09 s}.

   The whole frame is a pure function of SONG TIME t (the shared <audio> element's clock while it plays; a wall clock
   while audio is locked or missing), so seeking, pausing and the QA screenshots are exact. Every picture beat is keyed
   to a sung word (S.W(line, word)); every card text (names, "what" / "why" lines, limits) and every planet point comes
   from core-local/nos.js (nums()). Literal words on the stage are the lyric's own ("just a guess?", "MODIFIED") and
   the video's (the science ladder, "the ladder is now a shelf", the watered pot: script s1_06 / s5_03, facts 2.7).
   Acts, keyed to lyric-line indices (content/song.json order):
     ladder   0–2    the tri-fold board lands; a blue WHAT card and an amber WHY card pop on "different" / "jobs" and
                     FLIP ON EVERY DOWNBEAT through the band break while the SCIENCE LADDER (hypothesis → theory → law)
                     is built rung by rung; "tore": the ladder tears in two and falls; "isn't real": the title struck
                     in red; "Two cards side by side": the two cards slide in, side by side; "deal": taped up
     chorus1  3–6    a law card (superposition + the rock layers) flips to WHAT on "what"; a theory card (plate
                     tectonics + a plain planet) flips to WHY on "why"; DIFFERENT JOBS; "just a guess?" struck out
     verse2   7–10   NASA's eight planets on graph paper (T² against a³, the fitted line drawn on "equals … cubed",
                     the points on "Every planet"); Kepler's third law card (WHAT) on "pattern"; Newton's rule (the law
                     of universal gravitation, WHAT) and an amber WHY? card on "cause" that flips to general relativity
                     on "why"; "still not a law"
     bridge   11–14  HYPOTHESIS (a testable idea, TEST IT on "test"), THEORY (TESTED on "tested", the trophy on
                     "best"; new evidence, a MODIFIED patch sewn on on "patch"), a LAW with its limits ringed on "limits"
     chorus2  15–18  the chorus again: the law of universal gravitation (+ the ringed planet), cell theory (+ the
                     microscope)
     outro    19–20  the watered pot labeled THEORY: a ghost law card over it crossed out on "law" (it never sprouts a
                     law); the ladder is now a shelf: WHAT and WHY side by side on "Different jobs"; TESTED
   Karaoke (bottom band, torn cream paper): the current line in slab type, the sung word on a mustard scrap, "what" /
   "law" in law blue, "why" / "theory" in theory brass, the hook "Different jobs" in ransom letters, the next line faint.
   Controls: click / Space play-pause, ↺ restart (R), ⌂ map, ← → seek 5 s, click or drag the strip at the bottom;
   "Finish" once ≥ 90 % has been heard. The song's end (≥ 90 % heard) → Game.complete('song').
   Portrait phone: the stage on top, Dock.karaoke + ↺ ▶ ⌂ + the strip below.
   Long music (docs/WEBSITE.md §8.4): lesson.json preload.stream names the .m4a, so it plays through the shared
   <audio> element (Audio.media), never decoded into memory.
   SFX (tier song): card_flip on every flip, poster_thump when the board lands, tear on "tore", the grease pencil
   (chalk_squeak = shared scribble) on every red stroke, stamp on TESTED; none while the clock runs fast (tests).

   QA hooks: sc.song (S(i), E(i), W(i, word), beats), sc.plan.acts, sc.plan.flips (the band-break flip times),
   sc.debugAt(t) (freeze at song time t), sc.now(), sc.play(t), sc.pause(), sc.toggle(), sc.seek(t), sc.restart(),
   sc.done() / sc.finishStub() (stamp now), sc.lyricAt(t), sc.activeWord(t), sc.actAt(t), sc.cardsAt(t) (the two
   band-break cards: {law, theory} face 0 | 1), sc.heard, sc.ended, sc.audioReady; Levels.song.nums() (every card text
   and planet point the stage shows, from Nos).
   ========================================================================== */
(function () {
  const STEP = 'song';
  const TAKE_NAME = 'funk_alt_7';
  const AUDIO = 'music/song.m4a', WORDS = 'music/song_words.json';
  const KEY = 'song:jobs';
  const WOOD = "'Alfa Slab One'", TYPE = "'Special Elite'";
  const FC = () => window.Fair.C;
  const BAND_Y = 832, CUR_Y = 912, NEXT_Y = 1012, CUR_SIZE = 56, NEXT_SIZE = 36;
  const TABLE_Y = 790;
  const FADE = 0.35;                                  // act cross-fade (s)
  const THRESH = 0.9;                                 // share of the song heard for the stamp / "Finish"
  const CREDIT = 'AI-generated song · the science is real';
  const RED = '#c8321f', STICKY = '#f2d479', INK = '#2b2420';
  const fx = (name, alt) => (Audio.has(name) ? name : alt);

  /* ---------------- song timing ---------------- */
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9]/g, '');
  class Song {
    constructor(d) {
      this.d = d; this.lines = d.lines.filter(l => !l.unsung && l.words && l.words.length); this.dur = d.duration || 80;
      const b = d.beats || {};
      this.bpm = b.bpm || 104; this.bt = 60 / this.bpm; this.off = b.offset || 0;
      this.items = this.lines.map(l => ({ ...l, kind: 'line' })).concat((d.extras || []).map((x, j) => ({ ...x, i: 100 + j }))).sort((a, c) => a.s - c.s);
    }
    S(i) { return this.lines[i] ? this.lines[i].s : 1e6; }
    E(i) { return this.lines[i] ? this.lines[i].e : 1e6; }
    /* start of the n-th word of line i that matches `word` (exact, then prefix); the line start if none */
    W(i, word, n = 1) {
      const l = this.lines[i]; if (!l) return 1e6;
      const k = normW(word);
      for (const pass of [w => normW(w.w) === k, w => normW(w.w).startsWith(k)]) {
        let c = 0;
        for (const w of l.words) if (pass(w) && ++c === n) return w.s;
      }
      if (window.__songCueMiss) window.__songCueMiss.push(i + ':' + word);
      return l.s;
    }
    beatPos(t) { return (t - this.off) / this.bt; }
    sinceBeat(t) { const p = this.beatPos(t); return (p - Math.floor(p)) * this.bt; }
    pulse(t, k = 9) { return t < this.off ? 0 : Math.exp(-this.sinceBeat(t) * k); }
    downbeat(k) { return this.off + 4 * k * this.bt; }
  }

  /* ---------------- every card text and planet point on the stage: the model only (core-local/nos.js) ---------------- */
  let NUMS = null;
  function nums() {
    if (NUMS) return NUMS;
    const N = window.Nos, I = N.ITEMS;
    const pick = id => { const it = I[id]; return { id, name: it.name, short: it.short, kind: it.kind, job: it.job, kid: it.kid, limits: it.limits || null }; };
    const pl = N.PLANETS.map(p => N.planet(p.name));
    NUMS = {
      // the two choruses: a law (WHAT) and a theory (WHY) each, all SSA-named (DESIGN.md facts rules), + their props
      pairs: [
        { law: pick('superposition'), theory: pick('plateTectonics'), props: ['rock_layers', 'planet_plain'] },
        { law: pick('gravitation'), theory: pick('cellTheory'), props: ['planet', 'microscope'] },
      ],
      terms: { hypothesis: N.TERMS.hypothesis.kid, theory: N.TERMS.theory.kid, law: N.TERMS.law.kid },
      kepler: pick('kepler3'), grav: pick('gravitation'), gr: pick('generalRelativity'), limited: pick('conservationMass'),
      // verse 2: T² against a³ in thousands (Fair.graph), the inner four in their own inset
      planets: pl.map(p => ({ name: p.name, x: p.a3 / 1000, y: p.T2 / 1000, ratio: p.ratio })),
      inner: pl.slice(0, 4).map(p => ({ name: p.name, x: p.a3, y: p.T2 })),
    };
    return NUMS;
  }

  /* ---------------- the plan: acts, flips, sfx ---------------- */
  const L = { intro: 0, tore: 1, cards: 2, ch1: 3, v2: 7, br: 11, ch2: 15, out: 19 };
  function plan(S) {
    const b = i => S.S(i) - 0.4;
    const acts = [
      { id: 'ladder', at: -1, draw: actLadder },
      { id: 'chorus1', at: S.S(L.ch1) - 0.25, draw: actChorus, c0: L.ch1, pair: 0 },
      { id: 'verse2', at: b(L.v2), draw: actVerse2 },
      { id: 'bridge', at: S.S(L.br) - 0.05, draw: actBridge },
      { id: 'chorus2', at: b(L.ch2), draw: actChorus, c0: L.ch2, pair: 1 },
      { id: 'outro', at: b(L.out), draw: actOutro },
    ];
    acts.forEach((a, i) => { a.end = i + 1 < acts.length ? acts[i + 1].at : S.dur + 1; });
    // the band break: the ladder is built on the first three downbeats after the intro line, the cards flip on every
    // downbeat (law) and every half bar after it (theory) until "tore"
    const k0 = Math.ceil((S.E(L.intro) + 0.05 - S.off) / (4 * S.bt));
    const rungs = [0, 1, 2].map(j => S.downbeat(k0 + j));
    const tTore = S.W(L.tore, 'tore'), flips = [];
    for (let k = k0; S.downbeat(k) < tTore - 0.2; k++) { flips.push({ card: 'law', t: S.downbeat(k) }); if (S.downbeat(k) + 2 * S.bt < tTore - 0.2) flips.push({ card: 'theory', t: S.downbeat(k) + 2 * S.bt }); }
    const tStamp = S.E(L.out + 1) + 0.9;
    const sfx = [
      { t: 0.15, name: ['poster_thump', 'thunk'], gain: 0.4 },
      ...flips.map(f => ({ t: f.t, name: ['card_flip', 'paper'], gain: 0.22 })),
      { t: tTore, name: ['tear'], gain: 0.5 }, { t: S.W(L.tore, 'isnt'), name: ['chalk_squeak', 'scribble'], gain: 0.35 },
      { t: S.W(L.cards, 'deal'), name: ['paper'], gain: 0.3 },
    ];
    [L.ch1, L.ch2].forEach(c0 => sfx.push({ t: S.W(c0, 'what'), name: ['card_flip', 'paper'], gain: 0.35 }, { t: S.W(c0 + 1, 'why'), name: ['card_flip', 'paper'], gain: 0.35 },
      { t: S.W(c0 + 3, 'guess'), name: ['chalk_squeak', 'scribble'], gain: 0.35 }));
    sfx.push({ t: S.W(L.v2 + 1, 'pattern'), name: ['card_flip', 'paper'], gain: 0.3 }, { t: S.W(L.v2 + 2, 'Newton'), name: ['card_flip', 'paper'], gain: 0.3 },
      { t: S.W(L.v2 + 3, 'why'), name: ['card_flip', 'paper'], gain: 0.35 }, { t: S.W(L.v2 + 3, 'not'), name: ['chalk_squeak', 'scribble'], gain: 0.3 },
      { t: S.W(L.br, 'test'), name: ['stamp'], gain: 0.3 }, { t: S.W(L.br + 1, 'tested'), name: ['stamp'], gain: 0.35 },
      { t: S.W(L.br + 2, 'patch'), name: ['paper'], gain: 0.35 }, { t: S.W(L.br + 3, 'limits'), name: ['chalk_squeak', 'scribble'], gain: 0.3 },
      { t: S.W(L.out, 'law'), name: ['chalk_squeak', 'scribble'], gain: 0.35 },
      { t: S.W(L.out + 1, 'Different'), name: ['card_flip', 'paper'], gain: 0.3 }, { t: S.W(L.out + 1, 'jobs'), name: ['card_flip', 'paper'], gain: 0.3 });
    return { acts, ticks: acts.slice(1).map(a => a.at), rungs, flips, tStamp, sfx: sfx.sort((a, c) => a.t - c.t) };
  }
  function actAt(p, t) { let k = 0; for (let i = 0; i < p.acts.length; i++) if (t >= p.acts[i].at) k = i; return k; }
  /* the band-break cards' faces at t: the number of flips so far, mod 2 (0 = the big WHAT / WHY face) */
  function cardsAt(p, t) {
    const n = c => p.flips.filter(f => f.card === c && f.t <= t).length;
    return { law: n('law') % 2, theory: n('theory') % 2 };
  }

  /* ---------------- drawing helpers ---------------- */
  const layoutCache = new Map();
  const fontOk = () => !document.fonts || document.fonts.status === 'loaded';
  function measure(str, font) {
    const k = font + '|' + str;
    let w = layoutCache.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) layoutCache.set(k, w); }
    return w;
  }
  function ransomW(str, size, seed) {
    const k = 'R|' + str + '|' + size + '|' + seed;
    let w = layoutCache.get(k);
    if (w === undefined) { g.save(); w = ransom(str, -9999, -9999, size, 1e9, { seed }); g.restore(); if (fontOk()) layoutCache.set(k, w); }
    return w;
  }
  function withAlpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  const note = (str, x, y, size, t0, o = {}) => hand(str, x, y, size, t0, { color: FC().cream, dur: 0.4, ...o });
  const bounce = (t, t0, k = 6, a = 0.1) => (t >= t0 ? 1 + a * Math.exp(-(t - t0) * k) * Math.abs(Math.cos((t - t0) * 9)) : 1);
  const beatBob = (S, t) => 1 + 0.025 * S.pulse(t, 7);
  /* a prop cut-out standing on its base (x = centre, y = base, h = height) */
  function prop(name, x, y, h, s = 1, o = {}) {
    if (s <= 0.001 || !IMG[name]) return;
    cut(name, x, y, h, { ay: o.ay ?? 1, s, rot: o.rot || 0, flip: !!o.flip, seed: o.seed || 7, depth: 1.1, boil: false });
  }
  /* one face of a card: Fair.card (paper, title band, body, stamp) + an optional big slab word */
  function face(F, x, y, w, h, o = {}) {
    const s = o.s ?? 1, flip = o.flip ?? 1, rot = o.rot || 0;
    if (s <= 0.001) return null;
    const K = window.Fair.card(F.kind, x, y, w, h, { title: F.title || '', body: flip > 0.6 ? F.body || '' : '', stamp: flip > 0.6 ? F.stamp ?? false : false, seed: o.seed || 1, rot, s, flip, hi: o.hi || null });
    if (F.big && K) {
      const C = FC();
      g.save(); g.translate(x, y); g.rotate(rot); g.scale(s * flip, s);
      window.Fair.fitFont(F.big, w - 70, F.bigSize || 72, WOOD);
      g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = F.kind === 'law' ? C.lawInk : F.kind === 'theory' ? C.amberInk : C.hypoInk;
      g.fillText(F.big, 0, Math.round(h * 0.1));
      g.restore();
    }
    return K;
  }
  /* a card turning over (u 0..1) from one face to the other by x-scale */
  function flipCard(front, back, x, y, w, h, u, o = {}) {
    u = clamp(u);
    return face(u < 0.5 ? front : back, x, y, w, h, { ...o, flip: Math.max(0.04, Math.abs(Math.cos(Math.PI * u))) });
  }
  /* HOOK words as one ransom row, each word popping in on its sung word */
  function hookRow(words, t, x0, y, size, seed) {
    const gap = size * 0.5, ws = words.map(([w], k) => ransomW(w, size, seed + k)), tot = ws.reduce((a, b) => a + b, 0) + gap * (words.length - 1);
    let x = x0 - tot / 2;
    words.forEach(([w, t0], k) => {
      const cx = x + ws[k] / 2; x += ws[k] + gap;
      if (t < t0) return;
      const s = 1 + 0.12 * Math.exp(-(t - t0) * 7);
      g.save(); g.translate(cx, y); g.scale(s, s); ransom(w, 0, 0, size, t0, { seed: seed + k, stagger: 0.03 }); g.restore();
    });
  }
  function sticky(lines, x, y, t0, o = {}) {
    const s = pop(T, t0, 0.35, 2); if (s <= 0) return;
    const w = o.w || 320, size = o.size || 40, h = Math.round(40 + lines.length * size * 1.1), seed = o.seed || 610;
    g.save(); g.translate(x, y); g.rotate(o.rot ?? -0.05); g.scale(s, s);
    paperRect(0, 0, w, h, o.color || STICKY, { seed, depth: 1.2, boil: false });
    lines.forEach((l, i) => hand(l, 0, Math.round(-h / 2 + 20 + size * 0.6 + i * size * 1.1), size, t0 + 0.05, { color: INK, seed: seed + 1 + i, dur: 0.3 }));
    tape(0, -h / 2 + 2, 90, 0.05, undefined, seed + 9);
    if (o.strike != null && T >= o.strike) window.Fair.pencil([[-w / 2 + 14, 10], [0, -4], [w / 2 - 14, -14]], { prog: E.outCubic(P(T, o.strike, o.strike + 0.3)), seed: seed + 5 });
    g.restore();
  }
  const shape = (k, pts) => Object.assign(pts, { k: 'theory-song:' + k });
  const SH = { can: shape('can', [[-50, -44], [50, -44], [56, 44], [-56, 44]]), patch: shape('patch', [[-96, -38], [98, -42], [94, 40], [-100, 36]]), cap: shape('cap', [[0, -24], [70, 0], [0, 24], [-70, 0]]) };
  /* the storyboard's watering can (the video's doodle), pouring over [t0, t0 + 4.2] */
  function wateringCan(x, y, t, t0, s = 1) {
    if (s <= 0.001) return;
    const tip = E.inOutSine(P(t, t0, t0 + 0.5)) * -0.45;
    g.save(); g.translate(x, y); g.rotate(tip); g.scale(s, s);
    ink([[-40, -44], [-10, -86], [30, -44]], { w: 7, color: '#4f6f58', seed: 791, passes: 1, step: 8 });
    ink([[-50, 20], [-110, -26], [-122, -34]], { w: 9, color: '#4f6f58', seed: 792, passes: 1, step: 8 });
    paperPiece(SH.can, 0, 0, '#6f8f76', { seed: 793, depth: 1, boil: false });
    paperRect(0, -12, 90, 8, '#4f6f58', { seed: 794, depth: 0, boil: false });
    g.restore();
    if (t >= t0 + 0.4 && t < t0 + 4.2) for (let k = 0; k < 7; k++) {
      const u = (((t - t0) * 1.6 + k / 7) % 1 + 1) % 1, dx = x - 118 * s - 30 * u, dy = y - 10 * s + 120 * u * u + 40 * u;
      g.fillStyle = 'rgba(160,205,240,0.9)'; g.beginPath(); g.ellipse(dx, dy, 4, 7, 0.2, 0, TAU); g.fill();
    }
  }

  /* ---------------- the stage: the blueprint wall and the science-fair table ---------------- */
  function backdrop() {
    window.Fair.backdrop();
    window.Fair.table(960, TABLE_Y, 2100, { seed: 21 });
    window.Fair.floor(930);
  }

  /* ======================================================================
     the acts
     ====================================================================== */
  /* the band break and verse 1: the science ladder, built, then torn; two cards side by side */
  const BD = { x: 960, y: 470, w: 900, h: 540 };
  const RUNGS = [{ y: 190, word: 'HYPOTHESIS', kind: 'hypo' }, { y: 60, word: 'THEORY', kind: 'amber' }, { y: -70, word: 'LAW', kind: 'law' }];
  function ladder(p, t, seed = 300) {
    const C = FC(), rp = p.rungs;
    const rail = E.outCubic(P(t, rp[0] - 0.4, rp[0] + 0.2));
    if (rail > 0) {
      ink([[-120, 250], [-120, 250 - 380 * rail]], { w: 9, color: '#6b4a2a', seed: seed + 1, passes: 2, step: 30 });
      ink([[120, 250], [120, 250 - 380 * rail]], { w: 9, color: '#6b4a2a', seed: seed + 2, passes: 2, step: 30 });
    }
    RUNGS.forEach((r, j) => {
      const s = pop(T, rp[j], 0.35, 2.2); if (s <= 0) return;
      g.save(); g.translate(0, r.y); g.scale(s, s);
      const fill = r.kind === 'law' ? C.law : r.kind === 'amber' ? C.amber : C.hypo;
      paperRect(0, 0, 300, 62, fill, { seed: seed + 10 + j, depth: 0.8, boil: false });
      window.Fair.fitFont(r.word, 270, 36, WOOD); g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillStyle = r.kind === 'law' ? C.lawInk : r.kind === 'amber' ? C.amberInk : C.hypoInk; g.fillText(r.word, 0, 2);
      g.restore();
      if (j > 0) { const a = E.outCubic(P(t, rp[j] - 0.1, rp[j] + 0.3)); if (a > 0) inkArrow(-170, RUNGS[j - 1].y - 10, -170, RUNGS[j - 1].y - 10 - (RUNGS[j - 1].y - r.y - 20) * a, { w: 5, color: C.cream, seed: seed + 20 + j, head: 18 }); }
    });
    // the mortarboard on top: "graduation"
    const cs = pop(T, rp[2] + 0.5, 0.35, 2.2);
    if (cs > 0) {
      g.save(); g.translate(0, -140); g.scale(cs, cs); g.rotate(-0.08);
      paperRect(0, 12, 64, 24, '#222831', { seed: seed + 30, depth: 0.8, boil: false });
      paperPiece(SH.cap, 0, -8, '#2b2f38', { seed: seed + 31, depth: 1, boil: false });
      ink([[0, -8], [40, -2], [52, 26]], { w: 3, color: '#e0a33a', seed: seed + 32, passes: 1, step: 8 });
      g.restore();
    }
  }
  /* the tear: a zigzag down the middle; each half falls away */
  const ZIG = (() => { const r = rng(417), pts = []; for (let y = -260; y <= 300; y += 28) pts.push([(r() - 0.5) * 26, y]); return pts; })();
  function halfClip(side) {
    g.beginPath(); g.moveTo(side * 400, -300); g.lineTo(ZIG[0][0], -300);
    ZIG.forEach(([x, y]) => g.lineTo(x, y));
    g.lineTo(ZIG[ZIG.length - 1][0], 320); g.lineTo(side * 400, 320); g.closePath(); g.clip();
  }
  const LAW_FACE = { kind: 'law', title: 'LAW', big: 'WHAT' };
  const WHY_FACE = { kind: 'theory', title: 'THEORY', big: 'WHY' };
  function actLadder(S, t, A) {
    const n = nums(), C = FC(), p = A.plan;
    const tOne = S.W(L.intro, 'One'), tTwoN = S.W(L.intro, 'two'), tDiff = S.W(L.intro, 'different'), tJobs = S.W(L.intro, 'jobs');
    const tTore = S.W(L.tore, 'tore'), tIsnt = S.W(L.tore, 'isnt'), tReal = S.W(L.tore, 'real');
    const tTwo = S.W(L.cards, 'Two'), tSide = S.W(L.cards, 'side', 2), tDeal = S.W(L.cards, 'deal');
    // the board lands
    const bs = pop(T, 0.15, 0.4, 1.8);
    window.Fair.board(BD.x, BD.y, BD.w, BD.h, { title: t >= p.rungs[0] - 0.4 ? 'THE SCIENCE LADDER' : '', seed: 51, s: bs, art: true });
    const titleY = BD.y - BD.h / 2 + 60;
    if (t >= tIsnt) window.Fair.strike(BD.x, titleY, 330, { prog: E.outCubic(P(t, tIsnt, tIsnt + 0.35)), seed: 311 });
    if (t >= tReal) withAlpha(1 - P(t, tTwo - 0.1, tTwo + 0.2), () => note('not a real ladder', BD.x, titleY + 70, 46, tReal, { color: RED, seed: 312 }));
    // "One, two"
    withAlpha(1 - P(t, S.E(L.intro) + 0.2, S.E(L.intro) + 0.6), () => {
      if (t >= tOne) ransom('ONE', BD.x - 160, BD.y + 20, 80, tOne, { seed: 321, stagger: 0.04 });
      if (t >= tTwoN) ransom('TWO', BD.x + 160, BD.y + 20, 80, tTwoN, { seed: 322, stagger: 0.04 });
    });
    // the ladder, then the tear
    if (t < tTore) { g.save(); g.translate(BD.x, BD.y); ladder(p, t); g.restore(); }
    else {
      const u = P(t, tTore, tTore + 1.1), a = 1 - P(t, tTore + 0.7, tTore + 1.3);
      if (a > 0) [-1, 1].forEach(side => withAlpha(a, () => {
        g.save(); g.translate(BD.x + side * (30 * u + 160 * u * u), BD.y + 420 * u * u); g.rotate(side * 0.6 * u);
        halfClip(side); ladder(p, t);
        g.restore();
      }));
    }
    // the WHAT and WHY cards: at the sides, flipping on the beat; then side by side
    const face0 = cardsAt(p, t), move = E.inOutCubic(P(t, tTwo - 0.1, tSide + 0.1));
    const lawBack = { kind: 'law', title: 'LAW', body: n.terms.law }, whyBack = { kind: 'theory', title: 'THEORY', body: n.terms.theory };
    const put = (x0, x1) => lerp(x0, x1, move), W0 = 440, H0 = 320, grow = lerp(360 / 440, 1, move);
    const flipU = c => { const last = p.flips.filter(f => f.card === c && f.t <= t).pop(); return last ? P(t, last.t, last.t + 0.3) : 1; };
    const endFace = cardsAt(p, tTwo);
    const drawOne = (c, x0, x1, t0, front, back, seed) => {
      const s = pop(T, t0, 0.35, 2) * beatBob(S, t) * grow; if (s <= 0) return;
      let fr, bk, u;
      if (t < tTwo) { u = flipU(c); const shown = face0[c]; fr = (u < 1 ? 1 - shown : shown) ? back : front; bk = shown ? back : front; }
      else { fr = endFace[c] ? back : front; bk = front; u = endFace[c] ? P(t, tTwo, tTwo + 0.3) : 1; }   // side by side: the big WHAT / WHY faces
      const x = put(x0, x1), y = BD.y + lerp(0, 20, move);
      flipCard(fr, bk, x, y, W0, H0, u, { s, seed, rot: lerp(c === 'law' ? -0.04 : 0.04, c === 'law' ? -0.02 : 0.02, move) });
      if (t >= tDeal) { tape(x - W0 * 0.3, y - H0 / 2, 110, -0.12, undefined, seed + 5); tape(x + W0 * 0.3, y - H0 / 2, 110, 0.12, undefined, seed + 6); }
    };
    drawOne('law', 250, 720, tDiff, LAW_FACE, lawBack, 331);
    drawOne('theory', 1670, 1200, tJobs, WHY_FACE, whyBack, 341);
    if (t >= tDeal) note('different jobs, not ranks', BD.x, BD.y + 250, 44, tDeal + 0.2, { seed: 351, color: FC().navy });
  }

  /* the chorus: a law flips to WHAT, a theory flips to WHY; DIFFERENT JOBS; neither one's a guess */
  const CH = { law: { x: 640, y: 440 }, theory: { x: 1280, y: 440 }, w: 520, h: 360 };
  function actChorus(S, t, A) {
    const n = nums(), pr = n.pairs[A.pair], c0 = A.c0;
    const tLaw = S.W(c0, 'law'), tWhat = S.W(c0, 'what'), tTh = S.W(c0 + 1, 'theory'), tWhy = S.W(c0 + 1, 'why');
    const tDiff = S.W(c0 + 2, 'Different'), tJobs = S.W(c0 + 2, 'jobs'), tNeither = S.W(c0 + 3, 'neither'), tGuess = S.W(c0 + 3, 'guess');
    // the props: the law's at the left edge, the theory's at the right
    const [pl, pt] = pr.props, ps0 = pop(T, tLaw - 0.2, 0.4, 1.8), ps1 = pop(T, tTh - 0.2, 0.4, 1.8);
    if (pl === 'rock_layers') prop(pl, 195, TABLE_Y + 4, 200, ps0, { seed: 401 });
    else prop(pl, 200, 470, 150, ps0, { seed: 401, ay: 0.5, rot: -0.1 + Math.sin(t * 0.8) * 0.03 });
    if (pt === 'planet_plain') prop(pt, 1730, 560, 250, ps1, { seed: 402, ay: 0.5, rot: t * 0.05 });
    else prop(pt, 1745, TABLE_Y + 4, 330, ps1, { seed: 402 });
    // the two cards
    const bob = beatBob(S, t), jb = bounce(t, tDiff, 6, 0.06) * bounce(t, tJobs, 6, 0.06);
    const lawS = pop(T, A.at + 0.1, 0.35, 1.8) * bob * jb, thS = pop(T, tTh - 0.35, 0.35, 1.8) * bob * jb;
    flipCard({ kind: 'law', title: 'A LAW', big: pr.law.short, bigSize: 64 }, { kind: 'law', title: 'WHAT: ' + pr.law.short.toUpperCase(), body: pr.law.kid, stamp: true },
      CH.law.x, CH.law.y, CH.w, CH.h, P(t, tWhat - 0.05, tWhat + 0.3), { s: lawS, seed: 411 + A.pair * 20, rot: -0.025 });
    flipCard({ kind: 'theory', title: 'A THEORY', big: pr.theory.short, bigSize: 64 }, { kind: 'theory', title: 'WHY: ' + pr.theory.short.toUpperCase(), body: pr.theory.kid, stamp: true },
      CH.theory.x, CH.theory.y, CH.w, CH.h, P(t, tWhy - 0.05, tWhy + 0.3), { s: thS, seed: 421 + A.pair * 20, rot: 0.025 });
    hookRow([['DIFFERENT', tDiff], ['JOBS', tJobs]], t, 960, 190, 72, 431);
    sticky(['just a guess?'], 960, 710, tNeither - 0.15, { w: 300, seed: 441 + A.pair * 10, rot: 0.04, strike: tGuess });
  }

  /* verse 2: NASA's planets; the pattern (a law); Newton's rule; the why came later (a theory) */
  const GR = { x: 580, y: 470, w: 700, h: 560 };
  function actVerse2(S, t, A) {
    const n = nums(), C = FC(), l = L.v2;
    const tYear = S.W(l, 'year'), tEq = S.W(l, 'equals'), tCubed = S.W(l, 'cubed'), tEvery = S.W(l + 1, 'Every'), tLine = S.W(l + 1, 'line'), tPattern = S.W(l + 1, 'pattern');
    const tNewton = S.W(l + 2, 'Newton'), tRule = S.W(l + 2, 'rule'), tCause = S.W(l + 2, 'cause'), tWhy = S.W(l + 3, 'why'), tNot = S.W(l + 3, 'not');
    prop('telescope', 125, TABLE_Y + 4, 330, pop(T, A.at + 0.1, 0.4, 1.8), { seed: 501 });
    prop('planet', 1000, 165, 100, pop(T, tYear, 0.4, 1.8), { seed: 502, ay: 0.5, rot: -0.12 + Math.sin(t * 0.7) * 0.03 });
    const gs = pop(T, tYear - 0.2, 0.4, 1.8);
    if (gs > 0) {
      g.save(); g.translate(GR.x, GR.y); g.scale(gs, gs);
      const pts = n.planets.map(p => ({ x: p.x, y: p.y, label: p.name === 'Uranus' ? p.name : '' }));
      const G = window.Fair.graph(0, 0, GR.w, GR.h, { xmax: 30, ymax: 30, xticks: [0, 10, 20, 30], yticks: [0, 10, 20, 30], xLabel: 'distance cubed, a³ (thousands)', yLabel: 'year squared, T² (thousands)',
        points: pts, pointsT0: tEvery, line: v => v, prog: E.inOutSine(P(t, tEq, tCubed + 0.4)), seed: 511, title: 'NASA: the eight planets' });
      const nep = n.planets[n.planets.length - 1], np = G.at(nep.x, nep.y);
      if (t >= tEvery + 0.12 * 7) hand('Neptune', np.x - 18, np.y + 30, 24, -1e6, { dur: 0.01, align: 'right', color: INK, seed: 512 });
      if (t >= tEq) { const q = G.at(16, 7); hand('T² = a³', q.x, q.y, 58, tEq, { color: RED, seed: 513, align: 'left', dur: 0.5 }); }
      // the inner four crowd the corner: their own inset
      const zs = pop(T, tLine, 0.35, 1.8);
      if (zs > 0) {
        const q = G.at(7.5, 22);
        g.save(); g.translate(q.x, q.y); g.rotate(0.02); g.scale(zs * 0.8, zs * 0.8);
        window.Fair.graph(0, 0, 310, 270, { xmax: 4, ymax: 4, xticks: [0, 2, 4], yticks: [0, 2, 4], points: n.inner.map(p => ({ x: p.x, y: p.y, label: p.name === 'Earth' || p.name === 'Mars' ? p.name : '' })), pointsT0: tLine, line: v => v, prog: 1, seed: 521, title: 'the inner four, zoomed' });
        g.restore();
      }
      g.restore();
    }
    // the pattern: Kepler's third law, a description (WHAT); it steps aside for Newton
    const kOut = P(t, tNewton - 0.4, tNewton);
    withAlpha(1 - kOut, () => face({ kind: 'law', title: n.kepler.name.toUpperCase(), big: n.kepler.short, bigSize: 60, stamp: t >= tPattern + 0.3 }, 1400, 420, 600, 330,
      { s: pop(T, tPattern, 0.35, 1.8) * (1 - 0.3 * kOut), seed: 531, rot: -0.02 }));
    if (t >= tPattern + 0.3 && t < tNewton) note(n.kepler.kid, 1400, 640, 36, tPattern + 0.3, { seed: 532 });
    // Newton's rule (the law: WHAT) and the cause (WHY?), which the why came later to fill (general relativity)
    face({ kind: 'law', title: n.grav.short.toUpperCase(), body: n.grav.kid, stamp: t >= tRule }, 1230, 450, 440, 380, { s: pop(T, tNewton, 0.35, 1.8) * beatBob(S, t), seed: 541, rot: -0.02 });
    if (t >= tNewton) note("Newton's rule: a law", 1250, 228, 40, tNewton + 0.1, { seed: 542 });
    const whyFront = { kind: 'theory', title: 'THE CAUSE?', big: '?', bigSize: 150 }, whyBack = { kind: 'theory', title: n.gr.short.toUpperCase(), body: n.gr.kid, stamp: true };
    flipCard(whyFront, whyBack, 1690, 450, 400, 380, P(t, tWhy - 0.05, tWhy + 0.3), { s: pop(T, tCause - 0.1, 0.35, 1.8) * beatBob(S, t) * bounce(t, tWhy, 6, 0.06), seed: 551, rot: 0.03 });
    if (t >= tNot) {
      note('the why: still a theory, not a law', 1460, 700, 44, tNot, { color: C.cream, seed: 561 });
      window.Fair.pencil([[1180, 735], [1460, 742], [1740, 732]], { prog: E.outCubic(P(t, tNot + 0.2, tNot + 0.6)), seed: 562, w: 7 });
    }
  }

  /* the bridge: hypothesis (TEST IT), theory (TESTED, the trophy, the patch), law (its limits) */
  const BR = { y: 400, w: 480, h: 340, x: [380, 960, 1540] };
  function actBridge(S, t, A) {
    const n = nums(), C = FC(), l = L.br;
    const tHyp = S.W(l, 'hypothesis'), tTest = S.W(l, 'test'), tTheory = S.W(l + 1, 'theory'), tWhy = S.W(l + 1, 'why'), tTested = S.W(l + 1, 'tested'), tBest = S.W(l + 1, 'best');
    const tEv = S.W(l + 2, 'evidence'), tPatch = S.W(l + 2, 'patch'), tLaws = S.W(l + 3, 'Laws'), tLimits = S.W(l + 3, 'limits'), tBatch = S.W(l + 3, 'batch');
    const all = bounce(t, tBatch, 6, 0.07), bob = beatBob(S, t);
    // hypothesis: a testable idea
    face({ kind: 'hypothesis', title: 'HYPOTHESIS', body: n.terms.hypothesis, stamp: t >= tTest }, BR.x[0], BR.y, BR.w, BR.h, { s: pop(T, tHyp - 0.1, 0.35, 1.8) * bob * all * bounce(t, tTest, 6, 0.08), seed: 601, rot: -0.03 });
    // theory: the why, tested the best (+ the trophy); new evidence → a patch: modified
    face({ kind: 'theory', title: 'THEORY', body: n.terms.theory, stamp: t >= tWhy }, BR.x[1], BR.y, BR.w, BR.h, { s: pop(T, tTheory - 0.1, 0.35, 1.8) * bob * all, seed: 611, rot: 0.02 });
    if (t >= tTested) UI.stamp('TESTED', BR.x[1] + 20, BR.y + 40, { rot: -0.14, s: pop(T, tTested, 0.3, 2.2) * 0.62, color: RED });
    prop('trophy', BR.x[1], TABLE_Y + 4, 190, pop(T, tBest, 0.4, 2), { seed: 612 });
    if (t >= tEv - 0.1) {
      const u = E.inOutCubic(P(t, tEv - 0.1, tPatch - 0.05)), x = lerp(-150, BR.x[1] - 330, u), y = lerp(760, 690, u);
      withAlpha(1 - P(t, tPatch + 0.3, tPatch + 0.7), () => sticky(['new', 'evidence'], x, y, tEv - 0.1, { w: 220, size: 38, seed: 621, rot: -0.08 }));
    }
    const pw = pop(T, tPatch, 0.3, 2.4);
    if (pw > 0) {
      g.save(); g.translate(BR.x[1] + 120, BR.y + 118); g.rotate(-0.06); g.scale(pw, pw);
      paperPiece(SH.patch, 0, 0, C.cream, { seed: 631, depth: 0.8, boil: false });
      for (let k = 0; k < 14; k++) { const a = k / 14 * TAU; ink([[Math.cos(a) * 88, Math.sin(a) * 32], [Math.cos(a) * 80, Math.sin(a) * 28]], { w: 3, color: RED, seed: 632 + k, passes: 1 }); }
      hand('MODIFIED', 0, 2, 40, tPatch + 0.1, { color: RED, seed: 650, dur: 0.3 });
      g.restore();
    }
    // a law: a description, with limits (here: the law of conservation of mass)
    face({ kind: 'law', title: n.limited.short.toUpperCase(), body: n.limited.limits, stamp: t >= tLaws + 0.3 }, BR.x[2], BR.y, BR.w, BR.h, { s: pop(T, tLaws - 0.1, 0.35, 1.8) * bob * all, seed: 661, rot: -0.02 });
    if (t >= tLimits) {
      inkCircle(BR.x[2], BR.y - 62, 250, { w: 7, color: RED, seed: 671, sx: 1, sy: 0.3, step: 8, prog: E.outCubic(P(t, tLimits, tLimits + 0.4)) });
      note('laws have limits', BR.x[2], BR.y + 220, 46, tLimits + 0.2, { seed: 672 });
    }
  }

  /* the outro: the watered pot never sprouts a law; the ladder is now a shelf; TESTED */
  const POT = { x: 470, y: TABLE_Y + 6, h: 300 }, SHELF = { x: 1390, y: 640 };
  function actOutro(S, t, A) {
    const n = nums(), C = FC(), l = L.out;
    const tTheory = S.W(l, 'theory'), tNever = S.W(l, 'never'), tTurns = S.W(l, 'turns'), tLaw = S.W(l, 'law');
    const tDiff = S.W(l + 1, 'Different'), tJobs = S.W(l + 1, 'jobs'), tAll = S.W(l + 1, 'all'), tS = A.plan.tStamp;
    const ps = pop(T, A.at + 0.1, 0.4, 1.8);
    prop('flower_pot', POT.x, POT.y, POT.h, ps, { seed: 701 });
    if (ps > 0) {
      g.save(); g.translate(POT.x, POT.y - 110); g.rotate(-0.04); g.scale(ps, ps);
      paperRect(0, 0, 190, 58, C.amber, { seed: 702, depth: 0.8, boil: false });
      window.Fair.fitFont('THEORY', 170, 36, WOOD); g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = C.amberInk; g.fillText('THEORY', 0, 2);
      g.restore();
    }
    wateringCan(POT.x + 270, POT.y - 250, t, tTheory, pop(T, tTheory - 0.3, 0.35, 1.8));
    // the law it will never sprout: a ghost law card over the pot, crossed out on "law"
    const gs = pop(T, tTurns - 0.1, 0.35, 1.8);
    if (gs > 0) withAlpha(0.6, () => face({ kind: 'law', title: 'LAW', big: '?' }, POT.x, 290, 300, 200, { s: gs, seed: 711, rot: 0.04 }));
    if (t >= tLaw) {
      window.Fair.pencil([[POT.x - 170, 190], [POT.x + 170, 390]], { prog: E.outCubic(P(t, tLaw, tLaw + 0.25)), seed: 712, w: 11 });
      window.Fair.pencil([[POT.x + 170, 190], [POT.x - 170, 390]], { prog: E.outCubic(P(t, tLaw + 0.15, tLaw + 0.4)), seed: 713, w: 11 });
    }
    if (t >= tNever) note('water it all year: it never sprouts a law', POT.x, 440, 36, tNever, { seed: 714 });
    // the shelf: WHAT and WHY side by side
    const sh = pop(T, tDiff - 0.3, 0.35, 1.6);
    if (sh > 0) {
      g.save(); g.translate(SHELF.x, SHELF.y); g.scale(sh, 1);
      paperRect(0, 0, 900, 30, C.table, { seed: 721, depth: 1.2, boil: false, tex: 'tex_kraft' });
      ink([[-380, 14], [-380, 70]], { w: 7, color: '#6b4a2a', seed: 722, passes: 1 }); ink([[380, 14], [380, 70]], { w: 7, color: '#6b4a2a', seed: 723, passes: 1 });
      g.restore();
    }
    const bob = beatBob(S, t);
    face({ ...LAW_FACE, stamp: true }, SHELF.x - 225, SHELF.y - 175, 400, 300, { s: pop(T, tDiff, 0.35, 2) * bob, seed: 731, rot: -0.02 });
    face({ ...WHY_FACE, stamp: true }, SHELF.x + 225, SHELF.y - 175, 400, 300, { s: pop(T, tJobs, 0.35, 2) * bob, seed: 741, rot: 0.02 });
    hookRow([['DIFFERENT', tDiff], ['JOBS', tJobs]], t, SHELF.x, 190, 70, 751);
    if (t >= tAll) note('the ladder is now a shelf', SHELF.x, 272, 44, tAll, { seed: 761 });
    if (t >= tS) UI.stamp('TESTED', SHELF.x, SHELF.y + 92, { rot: -0.08, s: pop(T, tS, 0.3, 2.2) * 0.8, color: RED });
    void n;
  }

  function drawStage(S, p, t) {
    backdrop();
    const k = actAt(p, t), A = p.acts[k];
    const run = (i, a) => withAlpha(a, () => { g.save(); p.acts[i].draw(S, t, { ...p.acts[i], plan: p }); g.restore(); });
    if (k > 0 && t < A.at + FADE) run(k - 1, 1 - P(t, A.at, A.at + FADE));
    run(k, k === 0 ? 1 : P(t, A.at, A.at + FADE * 0.6));
  }

  /* ---------------- karaoke ---------------- */
  const HOOK = ['different', 'jobs'];
  const WORD_COL = () => { const C = FC(); return { what: C.law, law: C.law, laws: C.law, why: C.amberDeep, theory: C.amberDeep, hypothesis: C.navy }; };
  const wordColor = w => WORD_COL()[normW(w)] || null;
  function hookIdx(item) {
    const ws = item.words, out = new Set();
    for (let k = 0; k + HOOK.length <= ws.length; k++) if (HOOK.every((w, j) => normW(ws[k + j].w) === w)) HOOK.forEach((_, j) => out.add(k + j));
    return out;
  }
  function tokens(item) {
    const ws = item.words, hk = hookIdx(item), toks = [];
    for (let k = 0; k < ws.length; k++) {
      if (hk.has(k) && !hk.has(k - 1)) {
        const run = ws.slice(k, k + HOOK.length), last = run[run.length - 1];
        const trail = (last.w.match(/[,.!?]+$/) || [''])[0];
        toks.push({ text: run.map(w => w.w.replace(/[,.!?]+/g, '')).join(' ').toUpperCase(), s: run[0].s, e: last.e, ransom: true, trail });
        k += HOOK.length - 1; continue;
      }
      toks.push({ text: ws[k].w, s: ws[k].s, e: ws[k].e, col: wordColor(ws[k].w) });
    }
    return toks;
  }
  const lays = new Map();
  function layout(item, size) {
    const key = item.i + '|' + item.s + '|' + size;
    if (lays.has(key)) return lays.get(key);
    const toks = tokens(item), f = `${size}px ${WOOD}`, space = measure(' ', f) + size * 0.08, rs = size * 0.62;
    let x = 0;
    toks.forEach((tk, j) => {
      tk.seed = 400 + item.i * 13 + j * 5;
      if (tk.ransom) {
        tk.w = ransomW(tk.text, rs, tk.seed); tk.tw = tk.trail ? measure(tk.trail, f) : 0;
        tk.x = x + size * 0.1; x += tk.w + tk.tw + size * 0.2 + space;
      } else { tk.w = measure(tk.text, f); tk.x = x; x += tk.w + space; }
    });
    const out = { toks, width: x - space, size, rs };
    if (fontOk()) lays.set(key, out);
    return out;
  }
  const tokEnd = (toks, j) => { const tk = toks[j], nx = toks[j + 1]; return Math.min(Math.max(tk.e, tk.s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); };
  function drawCurrent(item, t, y, maxW = 1780) {
    let size = CUR_SIZE, Lo = layout(item, size);
    if (Lo.width > maxW) { size = Math.floor(size * maxW / Lo.width); Lo = layout(item, size); }
    const x0 = 960 - Lo.width / 2, C = FC();
    const appear = item.appear ?? item.s - 0.5, a = E.outCubic(P(t, appear, appear + 0.25)), yy = y + (1 - a) * 36;
    const f = `${size}px ${WOOD}`;
    Lo.toks.forEach((tk, j) => {
      const x = x0 + tk.x, sung = t >= tk.s, active = t >= tk.s - 0.02 && t < tokEnd(Lo.toks, j);
      if (tk.ransom) {
        if (!sung) withAlpha(0.3 * a, () => { g.font = `${Math.round(size * 0.8)}px ${TYPE}`; g.fillStyle = C.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(tk.text, x + tk.w / 2, yy); });
        else {
          const beat = 1 + 0.06 * Math.exp(-(t - tk.s) * 6);
          g.save(); g.translate(x + tk.w / 2, yy); g.scale(beat, beat);
          ransom(tk.text, 0, 0, Lo.rs, tk.s, { seed: tk.seed, stagger: clamp((tk.e - tk.s) / Math.max(1, tk.text.length), 0.02, 0.06) });
          g.restore();
        }
        if (tk.trail && sung) { g.save(); g.font = f; g.fillStyle = C.ink; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(tk.trail, x + tk.w + 6, yy); g.restore(); }
        return;
      }
      if (active) paperRect(x + tk.w / 2, yy + size * 0.04, tk.w + 24, size * 1.08, PAL.mustard, { seed: tk.seed, depth: 0.5, boil: false, s: 1 + 0.06 * Math.exp(-(t - tk.s) * 10) });
      g.save(); g.font = f; g.textAlign = 'left'; g.textBaseline = 'middle';
      if (!sung) { g.globalAlpha *= 0.3 * a; g.fillStyle = C.ink; g.fillText(tk.text, x, yy); }
      else {
        const p = E.outQuad(P(t, tk.s, tk.s + clamp(tk.e - tk.s, 0.08, 0.3)));
        if (p < 1) { g.save(); g.globalAlpha *= 0.3; g.fillStyle = C.ink; g.fillText(tk.text, x, yy); g.restore(); g.beginPath(); g.rect(x - 4, yy - size, (tk.w + 8) * p, size * 2); g.clip(); }
        g.fillStyle = active ? C.ink : tk.col || C.ink; g.fillText(tk.text, x, yy);
      }
      g.restore();
    });
  }
  /* a line comes up 0.5 s before its first word, but never before the previous line has been sung */
  const appearAt = (it, k) => (k === 0 ? it[0].s - 0.5 : Math.min(it[k].s, Math.max(it[k].s - 0.5, it[k - 1].e + 0.05)));
  function lyricAt(song, t) {
    const it = song.items;
    let cur = -1;
    for (let k = 0; k < it.length; k++) if (t >= appearAt(it, k)) cur = k;
    let c = cur >= 0 ? it[cur] : null;
    if (c) c.appear = appearAt(it, cur);
    const nx = it[cur + 1] || null;
    if (c && t > c.e + 2.2 && (!nx || nx.s - t > 1.2)) c = null;
    return { cur: c, next: nx && nx.s - t < 8 ? nx : null };
  }
  function activeWord(song, t) {
    const { cur } = lyricAt(song, t); if (!cur) return null;
    const ws = cur.words;
    for (let j = 0; j < ws.length; j++) { const nx = ws[j + 1], end = Math.min(Math.max(ws[j].e, ws[j].s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); if (t >= ws[j].s - 0.02 && t < end) return { line: cur.i, j, w: ws[j].w, s: ws[j].s, e: ws[j].e }; }
    return null;
  }
  const endNote = () => { const h = (Game.content.song && Game.content.song.hook) || []; return h.length >= 2 ? `${h[0]}. ${h[1]}.` : 'A law says what. A theory says why.'; };
  function band(song, t) {
    const C = FC();
    paperRect(960, BAND_Y + 140, 2040, 290, C.cream, { seed: 77, torn: true, depth: 1.8, boil: false });
    tape(110, BAND_Y + 6, 150, -0.08, undefined, 71); tape(1810, BAND_Y + 6, 150, 0.08, undefined, 72);
    const { cur, next } = lyricAt(song, t);
    const sec = (cur || next || {}).section;
    if (sec) { g.save(); g.font = `28px ${TYPE}`; g.fillStyle = 'rgba(31,53,82,0.75)'; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(sec.toLowerCase(), 70, BAND_Y + 36); g.restore(); }
    if (cur) drawCurrent(cur, t, CUR_Y);
    const nt = (txt, col, a) => { let size = NEXT_SIZE; const f = s => `${s}px ${TYPE}`; const w = measure(txt, f(size)); if (w > 1700) size = Math.floor(size * 1700 / w);
      g.save(); g.globalAlpha *= a; g.font = f(size); g.fillStyle = col; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(txt, 960, NEXT_Y); g.restore(); };
    if (next) nt(next.text, C.navy, 0.65);
    else if (song.items.length && t > song.items[song.items.length - 1].e) nt(endNote(), C.navy, 0.8);
    if (!cur && !next && t > 1) {
      const b = ((Math.floor(song.beatPos(t)) % 3) + 3) % 3, pu = song.pulse(t, 6);
      g.save(); g.font = `72px 'Segoe UI Symbol', 'DejaVu Sans', sans-serif`; g.fillStyle = C.navy; g.textAlign = 'center'; g.textBaseline = 'middle';
      ['♪', '♫', '♪'].forEach((nn, i) => g.fillText(nn, 860 + i * 100, CUR_Y - 24 * pu * (i === b ? 1 : 0.15)));
      g.restore();
    }
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class SongScene extends Scene {
    enter() {
      this.song = null; this.plan = null; this.err = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false;
      this.pos = 0; this.t0ctx = null; this.heard = 0; this.lastT = 0;
      this.audioReady = false; this.completed = false; this.gone = false;
      Audio.stopMusic();
      try { nums(); } catch (e) { this.err = 'nos model: ' + e.message; console.warn('[song]', this.err); }
      const wurl = Assets.url(WORDS);
      const words = (window.Loader ? Loader.json(wurl).then(d => d || Promise.reject(new Error('no timings'))) : fetch(wurl).then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))))
        .then(d => { this.song = new Song(d); this.plan = plan(this.song); })
        .catch(e => { this.err = String(e && e.message); console.warn('[song] no timings', this.err); });
      const audio = (Audio.has(KEY) ? Promise.resolve() : Audio.load({ [KEY]: Assets.url(AUDIO) })).then(() => { this.audioReady = Audio.has(KEY); });
      Promise.all([words, audio]).then(() => { if (!this.gone && !this.frozen && !this.startedOnce && this.song) this.play(0); });
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    exit() {
      this.gone = true; Audio.stopGroup('song'); this.playing = false;
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }
    get key() { return KEY; }
    /* the song clock: the <audio> element while it plays (t0ctx != null = "on the audio clock"), else pos */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el && m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false;
      from = clamp(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('song');
      this.pos = from; this.lastT = from; this.playing = true; this.t0ctx = null;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(KEY);
      if (this.media) {
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; }
          else if (!ok) { this.pause(); this.blocked = true; }
        });
      } else if (Audio.running()) {
        const at = Audio.now() + 0.06;
        Audio.schedule(KEY, { at, offset: from, gain: 1, group: 'song', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() {
      this.pos = this.now(); this.playing = false; this.t0ctx = null; this.playTok = (this.playTok || 0) + 1;
      Audio.stopGroup('song');
      if (this.media) this.media.pause();
    }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = clamp(t, 0, this.song.dur); this.lastT = this.pos; this.ended = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    /* QA: freeze on song time t (no pause overlay) */
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; this.lastT = t; }
    /* stamp the step now (the end of a ≥ 90 % listen, the "Finish" button, QA) */
    done() { if (this.completed) return; this.completed = true; if (!Game.progress.stamps[STEP]) Game.complete(STEP); }
    finishStub() { this.done(); }
    canFinish() { return !!this.song && !this.completed && this.heard >= THRESH * this.song.dur; }
    lyricAt(t) { return this.song ? lyricAt(this.song, t) : null; }
    activeWord(t) { return this.song ? activeWord(this.song, t) : null; }
    actAt(t) { return this.plan ? this.plan.acts[actAt(this.plan, t)].id : null; }
    cardsAt(t) { return this.plan ? cardsAt(this.plan, t) : null; }

    update(dt) {
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) &&
          (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      const now = this.now(), fast = window.__timeScale > 1;
      // the stage's sound effects, each on its sung word (skipped when the clock jumps: a seek or a fast test run)
      if (now > this.lastT && now - this.lastT < 0.5 && !fast) for (const c of this.plan.sfx) if (c.t > this.lastT && c.t <= now) { const nm = c.name.find(x => Audio.has(x)); if (nm) Audio.play(nm, { gain: c.gain }); }
      if (this.lastT < this.plan.tStamp && now >= this.plan.tStamp) Audio.play('stamp', { gain: 0.6 });
      this.lastT = now;
      if (now >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard >= THRESH * this.song.dur) this.done();
      }
    }
    keyDown(k) {
      if (!this.song) return;
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter' && this.ended) Game.replace(new Levels.map());
    }
    stripZone() { return Math.max(32, UI.tap(32)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }
      if (UI.inHud(x, y, 3)) return;
      if (this.finishRect && Math.abs(x - this.finishRect[0]) < this.finishRect[2] / 2 && Math.abs(y - this.finishRect[1]) < this.finishRect[3] / 2) return;
      if (y > 1080 - this.stripZone()) {
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); }
    pointerUp() {
      if (!this.scrub) return;
      const was = this.scrub.was; this.scrub = null; this.lastT = this.pos;
      if (was) this.play(this.pos);
    }

    draw() {
      const docked = UI.docked(), title = (Game.content.song && Game.content.song.title) || 'Different Jobs';
      if (!this.song || this.err) {
        window.Fair.backdrop();
        ransom('DIFFERENT JOBS', 960, 400, 100, this.enteredAt + 0.2, { seed: 42 });
        const msg = this.err ? 'the song is missing (' + this.err + ')' : 'tuning the bass…';
        hand(msg, 960, 580, 54, this.enteredAt + 0.4, { color: FC().cream, seed: 44 });
        if (docked) { Dock.head(title, ''); Dock.text('sg-wait', [msg], { cls: 'note center' }); this.dockBar(); }
        else this.chrome(null);
        return;
      }
      const t = clamp(this.now(), 0, this.song.dur), gT = T, gBF = BF;
      T = t; BF = Math.floor(t * 8);
      try {
        drawStage(this.song, this.plan, t);
        if (!docked) band(this.song, t);
      } finally { T = gT; BF = gBF; }
      if (docked) this.dock(t, title); else this.chrome(t);
    }

    /* ---------- the portrait dock: karaoke, controls and the strip below the stage ---------- */
    dock(t, title) {
      const S = this.song, pl = this.plan;
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head(title, `${mmss(t)} / ${mmss(S.dur)}`);
      const { cur, next } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      const lastE = S.items.length ? S.items[S.items.length - 1].e : 0;
      if (cur) {
        const key = cur.i + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) {
          const hk = hookIdx(cur);
          this._dk = { key, words: cur.words.map((w, j) => ({ w: w.w, s: w.s, e: w.e, hook: hk.has(j) })) };
        }
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '',
        note: t > lastE && S.items.length ? endNote() : this.startedOnce || this.frozen ? '♪ ♫ ♪' : 'loading the song…' });
      Dock.text('credit', ['♪ ' + CREDIT], { cls: 'small center' });
      if (this.ended && Game.current() === this) {
        Dock.buttons('sg-end', [{ label: '↺ Play it again', color: PAL.teal, onClick: () => this.restart() },
          { label: 'Back to the map', color: FC().lawDeep, onClick: () => Game.replace(new Levels.map()) }]);
      } else if (this.canFinish() && Game.current() === this) {
        Dock.buttons('sg-finish', [{ label: 'Finish', color: RED, onClick: () => this.done() }]);
      }
      Dock.strip({ value: t, total: S.dur, ticks: pl.ticks, label: 'song position', onSeek: (v, phase) => this.scrubTo(v, phase) });
      this.dockBar();
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) this.pausedMark(540, 150, 1080);
    }
    dockBar() {
      if (this.song && !this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the song from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => Game.replace(new Levels.map()) });
    }
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = clamp(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos; this.lastT = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (was) this.play(this.pos); }
    }
    /* the paused ▶: a dim wash and a mustard paper disc */
    pausedMark(y, r, h) {
      g.save(); g.fillStyle = 'rgba(27,21,18,0.28)'; g.fillRect(0, 0, 1920, h); g.restore();
      paperCircle(960, y, r, PAL.mustard, { seed: 63, depth: 1.6 });
      g.save(); g.fillStyle = PAL.ink; g.beginPath(); g.moveTo(960 - r * 0.32, y - r * 0.55); g.lineTo(960 + r * 0.55, y); g.lineTo(960 - r * 0.32, y + r * 0.55); g.closePath(); g.fill(); g.restore();
    }

    chrome(t) {
      const t0 = this.enteredAt, S = this.song, C = FC();
      ransom('DIFFERENT JOBS', 40, 62, 36, t0 + 0.1, { align: 'left', seed: 61 });
      hand('♪ ' + CREDIT, 44, 118, 28, t0 + 0.4, { align: 'left', color: C.cream, seed: 62, dur: 0.4 });
      const m = UI.hudSlot(0), r = UI.hudSlot(1), pl = UI.hudSlot(2), fx_ = { r: m.r, fixed: true };
      if (UI.iconButton('sg-map', '⌂', m.x, m.y, fx_)) { Game.replace(new Levels.map()); return; }
      if (S) {
        if (UI.iconButton('sg-restart', '↺', r.x, r.y, fx_)) this.restart();
        if (UI.iconButton('sg-play', this.playing ? '❚❚' : '▶', pl.x, pl.y, { ...fx_, color: PAL.mustard })) this.toggle();
      }
      if (t == null) return;
      const zone = this.stripZone(), touch = zone > 32;
      if (!this.ended) UI.target('sg-strip', 960, 1080 - zone / 2, 1920, zone);
      const sh = touch ? 22 : 12, sy = 1080 - sh;
      g.save();
      g.fillStyle = 'rgba(43,36,32,0.25)'; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = C.law; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = C.amberDeep; this.plan.ticks.forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      g.restore();
      if (touch || this.scrub) paperCircle(clamp(1920 * t / S.dur, 26, 1894), touch ? 1080 - 24 : sy + sh / 2, touch ? 22 : 14, C.lawDeep, { seed: 66, depth: 0.8, boil: false });
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended) {
        this.pausedMark(430, 100, BAND_Y);
        hand(UI.hint('paused · click or Space to play', 'paused · tap to play'), 960, 590, 48, -1e6, { dur: 0.01, color: PAL.cream, outline: 'rgba(31,53,82,0.92)', seed: 64 });
      }
      if (!this.startedOnce && !this.frozen) hand('loading the song…', 960, 590, 48, this.enteredAt + 0.3, { color: C.cream, seed: 65 });
      this.finishRect = null;
      if (this.ended && Game.current() === this) {
        if (UI.button('sg-again', '↺ Play it again', 700, 640, { w: 440, h: 110, color: PAL.teal, size: 56 })) this.restart();
        if (UI.button('sg-back', 'Back to the map', 1220, 640, { w: 440, h: 110, color: C.lawDeep, size: 56 })) Game.replace(new Levels.map());
      } else if (this.canFinish() && Game.current() === this) {
        const bw = 300, bh = UI.tap(90), bx = 1920 - 40 - bw / 2, by = m.y + m.r + 28 + bh / 2;
        this.finishRect = [bx, by, bw, bh];
        if (UI.button('sg-finish', 'Finish', bx, by, { w: bw, h: 90, color: RED, size: 52 })) this.done();
      }
    }
  }
  SongScene.STEP = STEP;
  SongScene.TAKE = TAKE_NAME;
  SongScene.Song = Song;
  SongScene.plan = plan;
  SongScene.nums = nums;
  SongScene.LINES = L;
  Levels.song = SongScene;
})();
